import * as React from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import type { Client } from '@/types';

interface ClientComboboxProps {
  clients: Client[];
  value?: string;
  onValueChange: (value: string, client: Client | undefined) => void;
  placeholder?: string;
  recentClientIds?: string[];
  maxRecentClients?: number;
  disabled?: boolean;
}

export function ClientCombobox({
  clients,
  value,
  onValueChange,
  placeholder = 'Select a client...',
  recentClientIds = [],
  maxRecentClients = 5,
  disabled = false,
}: ClientComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  // Get recent clients
  const recentClients = React.useMemo(() => {
    return recentClientIds
      .map((id) => clients.find((c) => String(c.id) === id))
      .filter((c): c is Client => c !== undefined)
      .slice(0, maxRecentClients);
  }, [clients, recentClientIds, maxRecentClients]);

  // Get selected client
  const selectedClient = clients.find((client) => String(client.id) === value);

  // Filter clients for modal table based on search
  const filteredClients = React.useMemo(() => {
    if (!searchQuery.trim()) return clients;
    
    const query = searchQuery.toLowerCase();
    return clients.filter(
      (client) =>
        client.name.toLowerCase().includes(query) ||
        client.code?.toLowerCase().includes(query) ||
        client.contactName?.toLowerCase().includes(query) ||
        client.contactEmail?.toLowerCase().includes(query)
    );
  }, [clients, searchQuery]);

  const handleSelect = (client: Client) => {
    onValueChange(String(client.id), client);
    setOpen(false);
    setModalOpen(false);
  };

  const handleShowMore = () => {
    setOpen(false);
    setModalOpen(true);
    setSearchQuery('');
  };

  return (
    <>
      {/* Main Combobox - Shows Recent Clients */}
      <Popover open={disabled ? false : open} onOpenChange={disabled ? undefined : setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between"
          >
            {selectedClient ? selectedClient.name : placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search recent clients..." />
            <CommandList>
              <CommandEmpty>No recent clients found.</CommandEmpty>
              
              {/* Recent Clients */}
              {recentClients.length > 0 && (
                <CommandGroup heading="Recent Clients">
                  {recentClients.map((client) => (
                    <CommandItem
                      key={client.id}
                      value={client.name}
                      onSelect={() => handleSelect(client)}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value === String(client.id) ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <div className="flex flex-col">
                        <span className="font-medium">{client.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {client.code && <>{client.code}</>}
                          {client.contactName && ` • ${client.contactName}`}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {/* Show More Button */}
              <CommandGroup>
                <CommandItem
                  onSelect={handleShowMore}
                  className="cursor-pointer justify-center font-medium text-primary"
                >
                  <ChevronsUpDown className="mr-2 h-4 w-4" />
                  Show More ({clients.length - recentClients.length} more clients)
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Modal Dialog - Shows All Clients in Table */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Select Client</DialogTitle>
            <DialogDescription>
              Choose a client from the list below. Click on a row to select.
            </DialogDescription>
          </DialogHeader>

          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name, code, contact..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Table Container with Scroll */}
          <div className="flex-1 overflow-auto border rounded-md">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead className="w-[40px]"></TableHead>
                  <TableHead>Client Name</TableHead>
                  <TableHead>Client Code</TableHead>
                  <TableHead>Contact Name</TableHead>
                  <TableHead>Contact Email</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      No clients found matching your search.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredClients.map((client) => (
                    <TableRow
                      key={client.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSelect(client)}
                      data-state={value === String(client.id) ? 'selected' : undefined}
                    >
                      <TableCell>
                        <Check
                          className={cn(
                            'h-4 w-4',
                            value === String(client.id) ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{client.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {client.code || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {client.contactName || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {client.contactEmail || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Results Count */}
          <div className="text-sm text-muted-foreground">
            Showing {filteredClients.length} of {clients.length} clients
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
